import React from 'react';
import { SalvButton, SalvCard, SalvTextField } from '@salvapp/ui-components';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import { useMe } from '../organisation/useMe';
import { parseErrorMessage } from '../common/errors/helpers';
import { useQueryBridgeApiConfiguration, useUpdateBridgeApiConfiguration } from './bridgeActions';
import './BridgeApiConfiguration.scss';
import { showFeedback } from '../feedback/feedbackReducer';
import { useDispatch } from 'react-redux';
import { currentEnvironment, EnvironmentNames } from '../tracking/environment';

export interface BridgeConfigurationSettingsFormValues {
  clientId: string;
  clientSecret: string;
  tokenUrl: string;
  organisationEmail: string;
  webhookSecret: string;
}

export const BridgeApiConfiguration = () => {
  const { data: bridgeApiConfiguration } = useQueryBridgeApiConfiguration();
  const { mutateAsync: updateBridgeApiConfiguration } = useUpdateBridgeApiConfiguration();
  const { features } = useMe();
  const dispatch = useDispatch();

  const isEnv = (envs: EnvironmentNames[]) => {
    const environment = currentEnvironment();
    if (!environment) return false;
    return envs.includes(environment.name);
  };

  const getDefaultTokenUrl = () => {
    const isDevelopment = isEnv([EnvironmentNames.DEVELOPMENT]);
    const isDemo = isEnv([EnvironmentNames.DEMO, EnvironmentNames.STAGING, EnvironmentNames.LHV_UK_UAT]);
    const isProduction = isEnv([EnvironmentNames.LHV_EE, EnvironmentNames.LHV_UK, EnvironmentNames.PRODUCTION]);

    if (isDevelopment) return 'http://localhost:3001/oauth/token';
    if (isDemo) return 'https://bridge-demo.salv.com/oauth/token';
    if (isProduction) return 'https://bridge.salv.com/oauth/token';

    return '';
  };

  const initialValues: BridgeConfigurationSettingsFormValues = {
    clientId: bridgeApiConfiguration?.clientId || '',
    clientSecret: bridgeApiConfiguration?.clientSecret || '',
    tokenUrl: bridgeApiConfiguration?.tokenUrl || getDefaultTokenUrl(),
    organisationEmail: bridgeApiConfiguration?.organisationEmail || '',
    webhookSecret: bridgeApiConfiguration?.webhookSecret || '',
  };

  const isBridgeWebhooksEnabled = isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_WEBHOOKS, features);

  const validationSchema = Yup.object().shape({
    clientId: Yup.string().required(),
    clientSecret: Yup.string().required('Required'),
    organisationEmail: Yup.string().email(),
    ...(isBridgeWebhooksEnabled && {
      webhookSecret: Yup.string().required('Required'),
    }),
  });

  const submit = async (
    values: BridgeConfigurationSettingsFormValues,
    { setSubmitting, resetForm }: FormikHelpers<BridgeConfigurationSettingsFormValues>
  ) => {
    setSubmitting(true);
    try {
      await updateBridgeApiConfiguration({
        tokenUrl: values.tokenUrl,
        clientId: values.clientId,
        clientSecret: values.clientSecret,
        organisationEmail: values.organisationEmail,
        webhookSecret: isBridgeWebhooksEnabled ? values.webhookSecret : undefined,
      });

      dispatch(
        showFeedback({
          message: 'Configuration updated',
          success: true,
        })
      );
    } catch (e: any) {
      resetForm();

      dispatch(
        showFeedback({
          message: await parseErrorMessage(e),
          success: false,
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SalvCard expansionOptions={{ isDefaultExpanded: true }}>
      <SalvCard.Header>Bridge API Configuration</SalvCard.Header>
      <SalvCard.Body>
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          onSubmit={submit}
          validationSchema={validationSchema}
          validateOnChange={false}
        >
          {({ isSubmitting, setFieldValue, values, errors }: FormikProps<BridgeConfigurationSettingsFormValues>) => {
            return (
              <Form className='bridge-api-configuration'>
                <SalvTextField
                  label='Token URL'
                  value={values.tokenUrl}
                  onChange={(value) => setFieldValue('tokenUrl', value)}
                  required
                />

                <SalvTextField
                  label='Client ID'
                  value={values.clientId}
                  onChange={(value) => setFieldValue('clientId', value)}
                  isInvalid={!!errors.clientId}
                  required
                />

                <SalvTextField
                  label='Client Secret'
                  value={values.clientSecret}
                  onChange={(value) => setFieldValue('clientSecret', value)}
                  isInvalid={!!errors.clientSecret}
                  required
                />

                <SalvTextField
                  label='Organisation notification email'
                  value={values.organisationEmail}
                  onChange={(value) => setFieldValue('organisationEmail', value)}
                  isInvalid={!!errors.organisationEmail}
                />
                {isBridgeWebhooksEnabled && (
                  <SalvTextField
                    label='Secret token for webhooks'
                    value={values.webhookSecret}
                    onChange={(value) => setFieldValue('webhookSecret', value)}
                    isInvalid={!!errors.webhookSecret}
                    required
                  />
                )}

                <div>
                  <SalvButton type='submit' variant='primary' disabled={isSubmitting} label='Save' />
                </div>
              </Form>
            );
          }}
        </Formik>
      </SalvCard.Body>
    </SalvCard>
  );
};

export default BridgeApiConfiguration;
